import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { CustomerSelect } from "@/components/customers/CustomerSelect";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useCreateOrder } from "@/hooks/use-orders";
import { ORDER_STATUS_OPTIONS } from "@/lib/enums";

interface OrderCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OrderCreateDialog({ open, onOpenChange }: OrderCreateDialogProps) {
  const navigate = useNavigate();
  const createOrder = useCreateOrder();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerLabel, setCustomerLabel] = useState<string>();
  const [status, setStatus] = useState("pending");

  function reset() {
    setCustomerId(null);
    setCustomerLabel(undefined);
    setStatus("pending");
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleCreate() {
    if (!customerId) return;
    createOrder.mutate(
      { customerId, status },
      {
        onSuccess: (order) => {
          handleOpenChange(false);
          navigate({ to: "/dashboard/orders/$orderId", params: { orderId: order.id } });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New order</DialogTitle>
          <DialogDescription>
            Pick a customer and starting status. You can add line items next.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Customer</Label>
            <CustomerSelect
              value={customerId}
              selectedLabel={customerLabel}
              onChange={(id, customer) => {
                setCustomerId(id);
                setCustomerLabel(customer.name);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <EnumSelect
              value={status}
              onValueChange={setStatus}
              options={ORDER_STATUS_OPTIONS}
              className="w-[180px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleCreate} disabled={!customerId || createOrder.isPending}>
            {createOrder.isPending ? "Creating…" : "Create order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
